<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, reactive, ref} from "vue";
import router from "@/router/index.js";
import {getRoute} from "@/hooks/getRoute.js";
import {listBicycles} from "@/api/bicycles/bicycles.js"
import {batchBindBicycleDeviceCodes} from "@/api/bicycleDeviceCodes/bicycleDeviceCodes.js"

const { route } = getRoute()
const qrCodeNo = computed(() => route.query.deviceCode || route.query.qrCodeNo || "")

/*搜索功能*/
// 表单数据
const formData = reactive({
  brandName: null,
  model: null,
})

/*表格数据展示*/
// 表格加载状态
const loading = ref(false)
// 虚拟表格锚点
const bindTableRef = ref()
// 虚拟表格滚动容器
let bindTableScrollWrapper = null
// 滚动加载状态
const loadMoreLoading = ref(false)
// 当前页码
const page = ref(1)
// 单页条数
const pageSize = 20
// 是否还有下一页
const hasNextPage = ref(false)
// 表格表头高度
const tableHeaderHeight = 56
// 表格行高
const tableRowHeight = 56
// 可选车辆总数
const totalCanBindQuantity = ref(0)

// 表格headers
const headers = [
  { title: "车辆品牌", key: "brandName", align: "center", nowrap: true },
  { title: "车辆型号", key: "model", align: "center", nowrap: true },
  { title: "车辆颜色", key: "color", align: "center", nowrap: true },
  { title: "车辆使用年龄段", key: "ageRange", align: "center", nowrap: true },
  { title: "车辆新旧程度", key: "conditionLevel", align: "center", nowrap: true },
  { title: "所属公司/品牌方", key: "providerSponsor", align: "center", nowrap: true },
  { title: "车辆当前放置场地", key: "currentVenue", align: "center", nowrap: true },
  { title: "车辆放置时间", key: "placementDate", align: "center", nowrap: true },
  { title: "未绑定数量", key: "unboundQuantity", align: "center", nowrap: true },
  { title: "绑定数量", key: "bindQuantity", align: "center", nowrap: true },
]
// 已加载表格数据
const tableData = ref([])

// 表格展示高度
const tableHeight = computed(() => {
  const visibleRows = Math.min(Math.max(tableData.value.length, 4), 8)
  return tableHeaderHeight + visibleRows * tableRowHeight
})

// 已选绑定数量
const selectedBindQuantity = computed(() => {
  return tableData.value.reduce((total, item) => total + (Number(item.bindQuantity) || 0), 0)
})

// 转换数值
const toNumber = (value) => {
  const result = Number(value)
  return Number.isFinite(result) ? result : 0
}

// 获取未绑定数量
const getUnboundQuantity = (item) => {
  const bicycleQuantity = toNumber(item.bicycleQuantity)
  const boundCount = toNumber(item.boundCount)
  return Math.max(bicycleQuantity - boundCount, 0)
}

// 处理车辆列表展示字段
const normalizeBicycleItem = (item) => {
  return {
    ...item,
    bicycleId: item.bicycleId || item.id,
    model: item.model || item.bicycleModel,
    color: item.color || item.bicycleColor,
    conditionLevel: item.conditionLevel || item.newness,
    providerSponsor: item.providerSponsor || item.sponsor,
    currentVenue: item.currentVenue || item.place,
    placementDate: item.placementDate || item.placeTime,
    unboundQuantity: getUnboundQuantity(item),
    bindQuantity: null,
  }
}

// 获取搜索参数
const getSearchParam = () => {
  return {
    brandName: formData.brandName,
    model: formData.model,
    viewType: "BRAND",
  }
}

// 滚动到表格顶部
const scrollTableTop = async () => {
  await nextTick()
  if (bindTableScrollWrapper) bindTableScrollWrapper.scrollTop = 0
}

// 获取车辆列表
const getBicycleList = async (isReset = false) => {
  if (!isReset && (!hasNextPage.value || loadMoreLoading.value)) return
  try {
    if (isReset) {
      page.value = 1
      tableData.value = []
      hasNextPage.value = false
      loading.value = true
      await scrollTableTop()
    } else {
      loadMoreLoading.value = true
    }
    const pageData = {
      page: page.value,
      pageSize,
    }
    const res = await listBicycles(pageData, getSearchParam())
    const list = (res.list || []).map(normalizeBicycleItem)
    tableData.value = isReset ? list : tableData.value.concat(list)
    totalCanBindQuantity.value = toNumber(res.bicycleTotalCount)
    hasNextPage.value = Boolean(res.hasNextPage)
    if (res.hasNextPage) page.value += 1
  } catch (e) {
    console.log(e)
  } finally {
    loading.value = false
    loadMoreLoading.value = false
  }
}

// 获取当前行可绑定最大数量
const getBindQuantityMax = (target) => {
  return toNumber(target.unboundQuantity)
}

// 处理绑定数量变化
const handleBindQuantityChange = (target) => {
  const max = getBindQuantityMax(target)
  const value = Number(target.bindQuantity) || 0
  target.bindQuantity = Math.min(Math.max(value, 0), max)
}

// 加载下一页表格数据
const loadNextPage = () => {
  getBicycleList(false)
}

// 处理虚拟表格滚动触底
const handleTableScroll = (e) => {
  const target = e.target
  if (target.scrollHeight - target.scrollTop - target.clientHeight > 24) return
  loadNextPage()
}

// 绑定虚拟表格滚动事件
const bindTableScroll = async () => {
  await nextTick()
  unbindTableScroll()
  bindTableScrollWrapper = bindTableRef.value?.$el?.querySelector(".v-table__wrapper")
  bindTableScrollWrapper?.addEventListener("scroll", handleTableScroll, { passive: true })
}

// 解绑虚拟表格滚动事件
const unbindTableScroll = () => {
  bindTableScrollWrapper?.removeEventListener("scroll", handleTableScroll)
  bindTableScrollWrapper = null
}

onMounted(() => {
  bindTableScroll()
  getBicycleList(true)
})

onBeforeUnmount(() => {
  unbindTableScroll()
})

/*操作功能*/
// 根据表单搜索信息
const handleSearch = async () => {
  await getBicycleList(true)
}
// 清空表单内容
const handleReset = () => {
  formData.brandName = null
  formData.model = null
  handleSearch()
}
// 处理取消
const handleCancel = () => {
  router.back()
}
// 处理确认绑定
const handleConfirmBind = async () => {
  const selectedRows = tableData.value.filter((item) => Number(item.bindQuantity) > 0)
  if (!selectedRows.length) return ElMessage.warning("请先填写绑定数量")
  if (selectedRows.some((item) => !item.bicycleId)) return ElMessage.error("车辆信息缺少ID，无法绑定")
  try {
    loading.value = true
    const param = selectedRows.map((item) => ({
      bicycleId: item.bicycleId,
      quantity: Number(item.bindQuantity),
    }))
    await batchBindBicycleDeviceCodes(param)
    ElMessage.success("绑定成功")
    await handleSearch()
  } catch (e) {
    console.log(e)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="container_box mg_b_2 bind-summary-box">
    <span class="bind-summary-title">绑定车辆</span>
    <span>（已选 {{ selectedBindQuantity }} 辆 / 总计可选 {{ totalCanBindQuantity }} 辆）：</span>
  </div>

  <div class="container_box mg_b_2 bind-search-box">
    <el-form :model="formData" class="bind-search-form" label-position="left">
      <el-form-item label="车辆品牌：">
        <el-input
            v-model="formData.brandName"
            class="bind-search-input"
            placeholder="请输入车辆品牌"
            clearable
        />
      </el-form-item>
      <el-form-item label="车辆型号：">
        <el-input
            v-model="formData.model"
            class="bind-search-input"
            placeholder="请输入车辆型号"
            clearable
        />
      </el-form-item>
      <div class="bind-search-actions">
        <v-btn color="success" size="large" @click="handleSearch">搜索</v-btn>
        <v-btn size="large" @click="handleReset">重置</v-btn>
      </div>
    </el-form>
  </div>

  <div class="container_box bind-table-box">
    <div class="bind-table-wrap">
      <v-data-table-virtual
          ref="bindTableRef"
          class="bind-virtual-table"
          :headers="headers"
          :items="tableData"
          :loading="loading"
          :height="tableHeight"
          item-value="bicycleId"
          :no-data-text="'没有更多数据了'"
          fixed-header
          hover
      >
        <template v-slot:loading>
          <v-skeleton-loader type="table-row@3"></v-skeleton-loader>
        </template>

        <template v-slot:item.bindQuantity="{ item }">
          <el-input-number
              v-model="item.bindQuantity"
              class="bind-quantity-input"
              :min="0"
              :max="getBindQuantityMax(item)"
              :step="1"
              :precision="0"
              :controls="false"
              placeholder="请输入数量"
              @change="handleBindQuantityChange(item)"
          />
        </template>

        <template v-slot:body.append>
          <tr class="bind-load-more-row">
            <td :colspan="headers.length">
              <div class="bind-load-more">
                <span v-if="loadMoreLoading">加载中...</span>
                <span v-else-if="!hasNextPage">没有更多数据了</span>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table-virtual>
    </div>

    <div class="bind-footer">
      <v-btn @click="handleCancel">取消</v-btn>
      <v-btn class="mg_l_4" color="primary" @click="handleConfirmBind">确认绑定</v-btn>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.bind-summary-box {
  padding: 24px 34px;
  text-align: left;
}

.bind-summary-title {
  font-weight: 500;
  color: #272539;
}

.bind-search-box {
  padding: 24px 50px;
  text-align: left;
}

.bind-search-form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px 48px;
}

.bind-search-form :deep(.el-form-item) {
  margin: 0;
}

.bind-search-input {
  width: 168px;
}

.bind-search-actions {
  display: flex;
  gap: 32px;
  margin-left: 90px;
}

.bind-table-box {
  box-sizing: border-box;
  width: 100%;
  min-height: 420px;
  padding: 40px 48px 90px;
  text-align: left;
  overflow: hidden;
}

.bind-table-wrap {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.bind-virtual-table {
  border: 1px solid #f1f2f5;
  width: 100%;
  min-width: 0;
}

.bind-virtual-table :deep(.v-table__wrapper) {
  overflow-x: auto;
}

.bind-virtual-table :deep(table) {
  min-width: 1120px;
}

.bind-virtual-table :deep(.v-data-table__th) {
  background: #f7f8fa;
}

.bind-quantity-input {
  width: 100px;
}

.bind-load-more-row td {
  border-bottom: none !important;
}

.bind-load-more {
  height: 32px;
  line-height: 32px;
  text-align: center;
  color: #909399;
  font-size: 12px;
}

.bind-footer {
  display: flex;
  align-items: center;
  margin-top: 90px;
  padding-left: 24px;
}
</style>
